import React, { useState, useEffect, useRef, useCallback } from 'react';
import { tutorials, getTutorialById } from './tutorials';

// ============================================================
// LEARN BY BUILDING - MULTI-TUTORIAL APP
// ============================================================
// EMBEDDING: ?tutorial=tutorial-id
// DEBUG MODE: ?tutorial=tutorial-id&debug=true
// ============================================================

// Screenshot aspect ratio - ALL screenshots must have this same ratio
const SCREENSHOT_ASPECT_RATIO = 1920 / 1064; // width / height

function App() {
  const urlParams = new URLSearchParams(window.location.search);
  const tutorialIdFromUrl = urlParams.get('tutorial');
  const debugParam = urlParams.get('debug');
  
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [tutorialStarted, setTutorialStarted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [score, setScore] = useState(0);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [debugMode, setDebugMode] = useState(debugParam === 'true');
  const [clickPosition, setClickPosition] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  
  const containerRef = useRef(null);
  const imageWrapperRef = useRef(null);

  useEffect(() => {
    if (tutorialIdFromUrl) {
      const tutorial = getTutorialById(tutorialIdFromUrl);
      if (tutorial) {
        setSelectedTutorial(tutorial);
      }
    }
  }, [tutorialIdFromUrl]);

  // Calculate image dimensions to fit container while maintaining aspect ratio
  const calculateDimensions = useCallback(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth - 32;
      const containerHeight = containerRef.current.clientHeight - 32;
      
      if (containerWidth <= 0 || containerHeight <= 0) return;
      
      let width, height;
      
      if (containerWidth / containerHeight > SCREENSHOT_ASPECT_RATIO) {
        height = containerHeight;
        width = height * SCREENSHOT_ASPECT_RATIO;
      } else {
        width = containerWidth;
        height = width / SCREENSHOT_ASPECT_RATIO;
      }
      
      const newWidth = Math.floor(width);
      const newHeight = Math.floor(height);
      
      setImageDimensions(prev => {
        if (prev.width !== newWidth || prev.height !== newHeight) {
          return { width: newWidth, height: newHeight };
        }
        return prev;
      });
    }
  }, []);

  // Use ResizeObserver for reliable dimension detection
  useEffect(() => {
    if (!tutorialStarted || !containerRef.current) return;
    
    // Initial calculation with delays to ensure DOM is ready
    calculateDimensions();
    const t1 = setTimeout(calculateDimensions, 100);
    const t2 = setTimeout(calculateDimensions, 300);
    
    // ResizeObserver for ongoing changes
    const resizeObserver = new ResizeObserver(() => {
      calculateDimensions();
    });
    
    resizeObserver.observe(containerRef.current);
    window.addEventListener('resize', calculateDimensions);
    
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateDimensions);
    };
  }, [tutorialStarted, calculateDimensions, currentStep]);

  const step = selectedTutorial?.steps[currentStep];
  const totalSteps = selectedTutorial?.steps.length || 0;
  const config = selectedTutorial?.config;

  const resetTutorial = () => {
    setCurrentStep(0);
    setScore(0);
    setWrongAttempts(0);
    setShowHint(false);
    setCompleted(false);
    setInputValue('');
    setClickPosition(null);
    setTutorialStarted(false);
    setImageDimensions({ width: 0, height: 0 });
  };

  const handleSelectTutorial = (tutorial) => {
    setSelectedTutorial(tutorial);
    setTutorialStarted(false);
    setCurrentStep(0);
    setScore(0);
    setWrongAttempts(0);
    setShowHint(false);
    setCompleted(false);
    setInputValue('');
    setClickPosition(null);
    setImageDimensions({ width: 0, height: 0 });
    window.history.pushState({}, '', `?tutorial=${tutorial.config.id}`);
  };

  const handleStartTutorial = () => {
    setImageDimensions({ width: 0, height: 0 });
    setTutorialStarted(true);
  };

  const handleCorrectClick = () => {
    if (step.requiresInput) {
      if (!inputValue.trim()) return;
      if (step.inputValidation && !step.inputValidation.test(inputValue)) {
        setWrongAttempts(prev => prev + 1);
        return;
      }
    }

    const stepScore = Math.max(100 - (wrongAttempts * 25), 25);
    setScore(prev => prev + stepScore);
    setWrongAttempts(0);
    setShowHint(false);
    setInputValue('');
    setClickPosition(null);

    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleCompletion();
    }
  };

  const handleWrongClick = (e) => {
    setWrongAttempts(prev => prev + 1);
    
    if (debugMode && imageWrapperRef.current) {
      const rect = imageWrapperRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width * 100).toFixed(1);
      const y = ((e.clientY - rect.top) / rect.height * 100).toFixed(1);
      setClickPosition({ x, y });
    }
  };

  const handleCompletion = () => {
    setCompleted(true);
    window.parent.postMessage({
      type: 'TUTORIAL_COMPLETE',
      event: 'lesson_complete',
      tutorialId: config.id,
      tutorial: config.title,
      score: score + Math.max(100 - (wrongAttempts * 25), 25),
      totalSteps: totalSteps,
      timestamp: new Date().toISOString(),
    }, '*');
  };

  // ============================================================
  // TUTORIAL SELECTOR (Development only)
  // ============================================================
  if (!selectedTutorial) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-yellow-800 text-sm">
              ⚠️ <strong>Development View:</strong> Users access via 
              <code className="bg-yellow-100 px-1 rounded ml-1">?tutorial=tutorial-id</code>
            </p>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Learn by Building</h1>
          <p className="text-gray-600 mb-8">Interactive tutorials for mastering Qualtrics</p>

          {tutorials.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500">No tutorials available yet.</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {tutorials.map((tutorial) => (
                <button
                  key={tutorial.config.id}
                  onClick={() => handleSelectTutorial(tutorial)}
                  className="bg-white rounded-lg shadow p-6 text-left hover:shadow-lg transition-shadow border-2 border-transparent hover:border-blue-500"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{tutorial.config.title}</h2>
                      <p className="text-sm text-gray-600 mt-1">{tutorial.config.description}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded
                      ${tutorial.config.difficulty === 'Beginner' ? 'bg-green-100 text-green-800' : ''}
                      ${tutorial.config.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' : ''}
                      ${tutorial.config.difficulty === 'Advanced' ? 'bg-red-100 text-red-800' : ''}
                    `}>
                      {tutorial.config.difficulty}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                    <span>⏱ {tutorial.config.estimatedTime}</span>
                    <span>📝 {tutorial.steps.length} steps</span>
                  </div>
                  <div className="mt-2 text-xs text-gray-400">ID: {tutorial.config.id}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================================================
  // INTRO SCREEN (Compact horizontal layout)
  // ============================================================
  if (!tutorialStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-6 max-w-4xl w-full">
          <div className="flex flex-col lg:flex-row lg:gap-8">
            {/* Left side: Title, description, and button */}
            <div className="flex-1 text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start gap-3 mb-3">
                <div 
                  className="inline-flex items-center justify-center w-12 h-12 rounded-full"
                  style={{ backgroundColor: `${config.brandColor}15` }}
                >
                  <span className="text-2xl">🎯</span>
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Let's practice!</h1>
              </div>
              
              <p className="text-lg font-medium text-gray-700 mb-2">{config.title}</p>
              <p className="text-gray-600 text-sm mb-4">{config.description}</p>
              
              {/* Stats */}
              <div className="flex justify-center lg:justify-start gap-4 mb-4 text-sm text-gray-500">
                <span>📝 {totalSteps} steps</span>
                <span>⏱ {config.estimatedTime}</span>
                <span>🏆 {totalSteps * 100} pts</span>
              </div>

              <button
                onClick={handleStartTutorial}
                className="w-full lg:w-auto px-8 py-3 rounded-xl text-white font-semibold hover:opacity-90 transition-opacity shadow-lg"
                style={{ backgroundColor: config.brandColor }}
              >
                Start Practice →
              </button>
            </div>

            {/* Right side: How it works (compact) */}
            <div className="flex-1 mt-6 lg:mt-0 lg:border-l lg:pl-8 border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">How it works</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div 
                    className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium"
                    style={{ backgroundColor: config.brandColor }}
                  >
                    1
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Read the challenge</p>
                    <p className="text-xs text-gray-500">Find the right place to click</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div 
                    className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium"
                    style={{ backgroundColor: config.brandColor }}
                  >
                    2
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Click on the screen</p>
                    <p className="text-xs text-gray-500">Find and click on the correct area of the screen to continue.</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div 
                    className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium"
                    style={{ backgroundColor: config.brandColor }}
                  >
                    3
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Use hints if needed</p>
                    <p className="text-xs text-gray-500">Stuck? Click "Need a hint?" for guidance on where to click.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // COMPLETION SCREEN
  // ============================================================
  if (completed) {
    const finalScore = score;
    const maxScore = totalSteps * 100;
    const percentage = Math.round((finalScore / maxScore) * 100);

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">
            {percentage === 100 ? '🏆' : percentage >= 75 ? '🎉' : '✅'}
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {percentage === 100 ? 'Perfect score!' : 'Practice Complete!'}
          </h2>
          <p className="text-gray-600 mb-6">You've completed "{config.title}"</p>

          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="text-4xl font-bold" style={{ color: config.brandColor }}>{finalScore}</div>
            <div className="text-sm text-gray-500">Your Score</div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
              <div className="h-2 rounded-full" style={{ width: `${percentage}%`, backgroundColor: config.brandColor }} />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Steps: {totalSteps}/{totalSteps} ✓</span>
              <span>Accuracy: {percentage}%</span>
            </div>
          </div>

          <button
            onClick={resetTutorial}
            className="w-full px-4 py-3 rounded-lg text-white hover:opacity-90"
            style={{ backgroundColor: config.brandColor }}
          >
            🔄 Try Again
          </button>

          {/* Feedback CTA */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500 mb-3">How was your experience?</p>
            <a
              href={`https://qxm.co1.qualtrics.com/jfe/form/SV_0HcPQkO5JBtWCR8?tutorial=${config.id}&score=${finalScore}&accuracy=${percentage}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium hover:underline"
              style={{ color: config.brandColor }}
            >
              <span>💬</span>
              <span>Share your feedback</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // MAIN TUTORIAL VIEW
  // ============================================================
  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Header */}
      <header 
        className="text-white px-4 py-3 flex items-center justify-between flex-shrink-0"
        style={{ backgroundColor: config.brandColor }}
      >
        <div>
          <h1 className="font-semibold">{config.title}</h1>
          <p className="text-xs text-white/80">{step.title}</p>
        </div>
        <div className="flex items-center gap-4">
          {debugParam === 'true' && (
            <button
              onClick={() => setDebugMode(!debugMode)}
              className={`text-xs px-2 py-1 rounded ${debugMode ? 'bg-yellow-400 text-yellow-900' : 'bg-white/20'}`}
            >
              {debugMode ? '🔧 Debug ON' : '⚙️'}
            </button>
          )}
          <div className="text-right">
            <div className="text-sm font-medium">{score} pts</div>
            <div className="text-xs text-white/80">Step {currentStep + 1}/{totalSteps}</div>
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="h-1 bg-gray-300 flex-shrink-0">
        <div
          className="h-full transition-all duration-300"
          style={{ width: `${((currentStep + 1) / totalSteps) * 100}%`, backgroundColor: config.brandColor }}
        />
      </div>

      {/* Main content */}
      <main className="flex-1 flex flex-col lg:flex-row min-h-0">
        
        {/* Screenshot area */}
        <div 
          ref={containerRef}
          className="flex-1 p-4 flex items-center justify-center bg-gray-200 min-h-0 overflow-hidden"
        >
          {imageDimensions.width > 0 ? (
            <div 
              ref={imageWrapperRef}
              className="relative rounded-lg shadow-lg overflow-hidden flex-shrink-0"
              style={{ 
                width: `${imageDimensions.width}px`, 
                height: `${imageDimensions.height}px`,
              }}
            >
              <img
                src={process.env.PUBLIC_URL + step.screenshot}
                alt={`Step ${step.id}`}
                className="absolute inset-0 w-full h-full object-cover"
                onClick={handleWrongClick}
                draggable={false}
              />

              <div
                onClick={(e) => { e.stopPropagation(); handleCorrectClick(); }}
                className={`absolute cursor-pointer ${debugMode ? 'border-2 border-dashed border-red-500 bg-red-500/20' : ''}`}
                style={{
                  top: `${step.hotspot.top}%`,
                  left: `${step.hotspot.left}%`,
                  width: `${step.hotspot.width}%`,
                  height: `${step.hotspot.height}%`,
                }}
              />

              {step.requiresInput && step.inputField && (
                <div
                  className="absolute"
                  style={{
                    top: `${step.inputField.top}%`,
                    left: `${step.inputField.left}%`,
                    width: `${step.inputField.width}%`,
                    height: `${step.inputField.height}%`,
                  }}
                >
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={step.inputPlaceholder}
                    className="w-full h-full px-2 text-sm border-2 border-blue-500 rounded bg-white/90"
                    onKeyPress={(e) => e.key === 'Enter' && handleCorrectClick()}
                  />
                </div>
              )}

              {debugMode && clickPosition && (
                <div className="absolute bottom-2 left-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
                  Click: {clickPosition.x}%, {clickPosition.y}%
                </div>
              )}
            </div>
          ) : (
            <div className="text-gray-400">Loading...</div>
          )}
        </div>

        {/* Instruction panel */}
        <div className="lg:w-80 bg-white p-6 border-t lg:border-t-0 lg:border-l flex-shrink-0 overflow-auto">
          <div className="mb-4">
            <span 
              className="text-xs font-medium px-2 py-1 rounded"
              style={{ backgroundColor: `${config.brandColor}20`, color: config.brandColor }}
            >
              Step {step.id} of {totalSteps}
            </span>
          </div>

          <h2 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h2>
          <p className="text-gray-600 mb-6">{step.instruction}</p>

          {step.requiresInput && !step.inputField && (
            <div className="mb-4">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={step.inputPlaceholder}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {showHint ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <span>💡</span>
                <p className="text-sm text-yellow-800">{step.hint}</p>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowHint(true)} className="text-sm text-gray-500 hover:text-gray-700 mb-4">
              💡 Need a hint?
            </button>
          )}

          {wrongAttempts > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{step.wrongClickMessage}</p>
              <p className="text-xs text-red-600 mt-1">Attempts: {wrongAttempts}</p>
            </div>
          )}

          {debugMode && (
            <div className="mt-6 p-4 bg-gray-900 text-gray-100 rounded-lg text-xs font-mono">
              <div className="text-yellow-400 mb-2">🔧 Debug Mode</div>
              <div>Container: {imageDimensions.width}×{imageDimensions.height}px</div>
              <div>Hotspot: top:{step.hotspot.top}% left:{step.hotspot.left}%</div>
              <div>Size: {step.hotspot.width}% × {step.hotspot.height}%</div>
              {clickPosition && (
                <div className="text-red-400 mt-2">Last click: {clickPosition.x}%, {clickPosition.y}%</div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default App;
